/**
 * BrandPanel — CyberFind-branded brand/preview panel for the signed-out
 * sign-in surface.
 *
 * CyberFind is the trust/brand anchor; IntelHub is the product/workspace being
 * entered. The preview uses non-sensitive product-level sample data only. It
 * never exposes real intelligence, customer/member data, CVEs, actors, victims,
 * counts, severity colors, or live timestamps. The preview is decorative for
 * assistive tech.
 *
 * Pure UI: no hooks, no routing. The auth surface (LoginPage) owns the footer
 * legal links, so this panel intentionally has none.
 */

const PREVIEW_METRICS = [
  {
    label: "Signal volume",
    value: "Live intake",
    detail: "Exploit, vendor, and regulatory signals",
  },
  {
    label: "Source coverage",
    value: "Curated feeds",
    detail: "Advisories, CERTs, vendors, and research",
  },
  {
    label: "Briefing queue",
    value: "Ready for review",
    detail: "Analyst notes prepared for decisions",
  },
] as const;

const PREVIEW_ROWS = [
  { indicator: "Exploit activity", scope: "Tracked", action: "Prioritise" },
  { indicator: "Vendor exposure", scope: "Mapped", action: "Brief" },
  { indicator: "Regulatory change", scope: "Watched", action: "Review" },
] as const;

export function BrandPanel() {
  return (
    <aside className="flex h-full flex-col justify-between gap-10 bg-muted p-10">
      {/* CyberFind brand anchor — decorative; the eyebrow names the brand in text. */}
      <picture className="block">
        <img
          src="/icons/cyberfind-icon-light.svg"
          alt=""
          aria-hidden
          className="h-8 w-auto dark:hidden"
        />
        <img
          src="/icons/cyberfind-icon-dark.svg"
          alt=""
          aria-hidden
          className="hidden h-8 w-auto dark:block"
        />
      </picture>

      <div className="space-y-8">
        <div className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wide text-foreground">
            CyberFind IntelHub
          </p>
          <h2 className="max-w-sm text-3xl font-semibold leading-tight text-foreground">
            Cybersecurity signals for the decisions ahead.
          </h2>
        </div>

        {/* Decorative preview with product-level sample data only. */}
        <div
          data-testid="brand-preview"
          aria-hidden="true"
          className="space-y-4"
        >
          {/* KPI-card-like blocks with product-level preview values. */}
          <div className="grid grid-cols-3 gap-3">
            {PREVIEW_METRICS.map((metric) => (
              <div
                key={metric.label}
                className="rounded-md border border-border bg-card p-3"
              >
                <span className="text-xs font-medium text-muted-foreground">
                  {metric.label}
                </span>
                <p className="mt-3 text-sm font-semibold text-foreground">
                  {metric.value}
                </p>
                <p className="mt-1 text-xs leading-snug text-muted-foreground">
                  {metric.detail}
                </p>
              </div>
            ))}
          </div>

          {/* Non-sensitive sample queue rows, not live intelligence. */}
          <div className="space-y-3 rounded-md border border-border bg-card p-4">
            {PREVIEW_ROWS.map((row) => (
              <div
                key={row.indicator}
                className="grid grid-cols-[1fr_auto_auto] items-center gap-3"
              >
                <span className="min-w-0 truncate text-xs font-medium text-foreground">
                  {row.indicator}
                </span>
                <span className="rounded-full bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
                  {row.scope}
                </span>
                <span className="rounded-full border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground">
                  {row.action}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-foreground">
        © 2026 CyberFind · Frankfurt
      </p>
    </aside>
  );
}
